import React, { useMemo, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import {
  type LensSchedule,
  useDeleteLensSchedule,
  useLensSchedules,
  useSaveLensSchedule,
  useSendLensScheduleNow,
  useSendLensScheduleTest,
} from '~/hooks/apis/reporting/useLensSchedules';

import { type Lens, type LensFilterValues, type LensPeriod, isCardLens } from '~/types/leLens';

import LensRecipientInput from '../LensRecipientInput';
import PrintSettingsFields from '../print/PrintSettingsFields';
import { DEFAULT_PRINT_SETTINGS } from '../print/printSettings';
import {
  COMPARE_TO_PRESETS,
  DATE_RANGE_PRESETS,
  matchingCompareTo,
  matchingDateRangePreset,
} from '../lensPeriodPresets';

import { reportTabSummary, selectedReportTabs, supportsTabScope, workbookScopeAvailable } from './reportScope';
import {
  ALLOWED_RECIPIENT,
  SCHEDULE_LIMITS,
  type ScheduleDraft,
  WEEKDAYS,
  describeFilters,
  draftProblem,
  emptyDraft,
  isFixedPeriod,
  isValidSendTime,
  labelFor,
  lensHasCards,
  lensHasFilters,
  mergeSets,
  ordinal,
  scheduleFilters,
  scheduleDescription,
  scheduleTiming,
  screenFilters,
  setsForEachValue,
} from './scheduleForm';
import { activeScheduleRun, runSeverity, runSummary } from './scheduleRun';

type Props = {
  open: boolean;
  onClose: () => void;
  lens: Lens;
  /** The reader's filter values on screen (saved defaults are added here). */
  screenValues: LensFilterValues;
  /** The values each select filter can take, as the board's filter controls list them. */
  filterOptions: Record<string, Array<string>>;
  userEmail?: string;
  allowPeriodOverride?: boolean;
  initialPeriod?: LensPeriod | null;
};

const DEFAULT_ID = 'lensDefault';
const CUSTOM_ID = 'custom';

const errorText = (error: unknown) => (error instanceof Error ? error.message : 'Something went wrong. Try again.');

function PeriodFields({
  value,
  onChange,
}: {
  value: LensPeriod | null;
  onChange: (period: LensPeriod | null) => void;
}) {
  const rangeId = !value
    ? DEFAULT_ID
    : isFixedPeriod(value)
      ? CUSTOM_ID
      : (matchingDateRangePreset(value)?.id ?? CUSTOM_ID);
  const fixed = value && isFixedPeriod(value) ? (value.window as { from: string; to: string }) : null;
  return (
    <Stack direction={{ sm: 'row', xs: 'column' }} spacing={1.5}>
      <TextField
        select
        label="Date range"
        size="small"
        sx={{ minWidth: 220 }}
        value={rangeId}
        onChange={(event) => {
          const id = event.target.value;
          if (id === DEFAULT_ID) {
            onChange(null);
          } else if (id === CUSTOM_ID) {
            onChange({ window: { from: '', to: '' }, comparison: value?.comparison ?? null });
          } else {
            const preset = DATE_RANGE_PRESETS.find((choice) => choice.id === id);
            onChange({
              ...(preset.anchor ? { anchor: preset.anchor } : {}),
              window: preset.window,
              comparison: value?.comparison ?? { back: 1, unit: 'year' },
            });
          }
        }}
      >
        <MenuItem value={DEFAULT_ID}>Lens default</MenuItem>
        {DATE_RANGE_PRESETS.map((preset) => (
          <MenuItem key={preset.id} value={preset.id}>
            {preset.label}
          </MenuItem>
        ))}
        <MenuItem value={CUSTOM_ID}>Fixed dates…</MenuItem>
      </TextField>
      {fixed && (
        <>
          <TextField
            label="From"
            size="small"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={fixed.from}
            onChange={(event) => onChange({ ...value, window: { ...fixed, from: event.target.value } })}
          />
          <TextField
            label="To"
            size="small"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={fixed.to}
            onChange={(event) => onChange({ ...value, window: { ...fixed, to: event.target.value } })}
          />
        </>
      )}
      {value && (
        <TextField
          select
          label="Compare to"
          size="small"
          sx={{ minWidth: 200 }}
          value={matchingCompareTo(value.comparison)?.id ?? 'none'}
          onChange={(event) =>
            onChange({
              ...value,
              comparison: COMPARE_TO_PRESETS.find((preset) => preset.id === event.target.value)?.comparison ?? null,
            })
          }
        >
          {COMPARE_TO_PRESETS.map((preset) => (
            <MenuItem key={preset.id} value={preset.id}>
              {preset.label}
            </MenuItem>
          ))}
        </TextField>
      )}
    </Stack>
  );
}

function ScheduleEditor({
  lens,
  initial,
  screenValues,
  filterOptions,
  onCancel,
  onSave,
  saving,
  saveError,
  allowPeriodOverride,
}: {
  lens: Lens;
  initial: ScheduleDraft;
  screenValues: LensFilterValues;
  filterOptions: Record<string, Array<string>>;
  onCancel: () => void;
  onSave: (draft: ScheduleDraft) => void;
  saving: boolean;
  saveError: string | null;
  allowPeriodOverride: boolean;
}) {
  const [draft, setDraft] = useState<ScheduleDraft>(initial);
  const [addingVersion, setAddingVersion] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [eachFilterId, setEachFilterId] = useState('');
  const [eachValues, setEachValues] = useState<Array<string>>([]);
  const [recipient, setRecipient] = useState('');
  const [recipientError, setRecipientError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const base = useMemo(() => screenFilters(lens, screenValues), [lens, screenValues]);
  const selectFilters = scheduleFilters(lens).filter((filter) => filter.type === 'select');
  const eachFilter = selectFilters.find((filter) => filter.id === eachFilterId);
  const eachOptions = eachFilter
    ? filterOptions[eachFilter.id]?.length
      ? filterOptions[eachFilter.id]
      : (eachFilter.options ?? [])
    : [];
  const problem = draftProblem(draft, lens);
  const hasCards = lensHasCards(lens);
  const hasFilters = lensHasFilters(lens);
  const room = SCHEDULE_LIMITS.filterSets - draft.filterSets.length;
  const update = (patch: Partial<ScheduleDraft>) => setDraft((current) => ({ ...current, ...patch }));
  const addSets = (sets: ScheduleDraft['filterSets']) => {
    const merged = mergeSets(
      lens,
      draft.filterSets,
      sets.map((set) =>
        supportsTabScope(lens) ? { scope: 'selected' as const, tabs: selectedReportTabs(lens), ...set } : set,
      ),
    );
    update({ filterSets: merged.sets });
    setNotice(
      merged.skipped
        ? `${merged.skipped} not added: a PDF with the same name or the same filters is already in the list, or the list is full (${SCHEDULE_LIMITS.filterSets} PDFs).`
        : null,
    );
  };
  const addRecipient = () => {
    const email = recipient.trim().toLowerCase();
    if (!ALLOWED_RECIPIENT.test(email)) {
      setRecipientError('Enter a luxuryescapes.com address.');
      return;
    }
    if (!draft.recipients.includes(email)) {
      update({ recipients: [...draft.recipients, email] });
    }
    setRecipient('');
    setRecipientError(null);
  };

  return (
    <>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <TextField
            fullWidth
            label="Email subject"
            size="small"
            value={draft.name}
            inputProps={{ maxLength: SCHEDULE_LIMITS.name }}
            onChange={(event) => update({ name: event.target.value })}
          />
          <Stack direction={{ sm: 'row', xs: 'column' }} spacing={1.5}>
            <TextField
              select
              id="lens-schedule-frequency"
              label="Frequency"
              size="small"
              sx={{ width: { xs: '100%', sm: 220 }, flexShrink: 0 }}
              value={draft.recurrence}
              onChange={(event) => update({ recurrence: event.target.value as ScheduleDraft['recurrence'] })}
            >
              <MenuItem value="daily">Every day</MenuItem>
              <MenuItem value="weekly">Selected weekdays</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
            </TextField>
            {draft.recurrence === 'monthly' && (
              <TextField
                select
                id="lens-schedule-month-day"
                label="Day of the month"
                size="small"
                sx={{ width: { xs: '100%', sm: 170 }, flexShrink: 0 }}
                value={draft.dayOfMonth}
                onChange={(event) => update({ dayOfMonth: Number(event.target.value) })}
              >
                {Array.from({ length: 28 }, (_, index) => index + 1).map((day) => (
                  <MenuItem key={day} value={day}>
                    {ordinal(day)}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              required
              error={touched && !isValidSendTime(draft.sendTime)}
              helperText={
                touched && !isValidSendTime(draft.sendTime) ? 'Choose a time from 00:00 to 23:59.' : undefined
              }
              inputProps={{ step: 60 }}
              InputLabelProps={{ shrink: true }}
              label="Send time"
              size="small"
              sx={{ width: { xs: '100%', sm: 180 }, flexShrink: 0 }}
              type="time"
              value={draft.sendTime}
              onChange={(event) => update({ sendTime: event.target.value })}
            />
          </Stack>

          {draft.recurrence === 'weekly' && (
            <Autocomplete
              multiple
              options={[1, 2, 3, 4, 5, 6, 7]}
              value={draft.weekdays ?? []}
              getOptionLabel={(day) => WEEKDAYS[day - 1]}
              onChange={(_, weekdays) => update({ weekdays: weekdays.sort((a, b) => a - b) })}
              renderInput={(params) => <TextField {...params} label="Weekdays" size="small" />}
            />
          )}
          <Stack direction={{ sm: 'row', xs: 'column' }} spacing={1.5}>
            <TextField
              label="Time zone"
              size="small"
              value={draft.timezone ?? 'Australia/Sydney'}
              onChange={(event) => update({ timezone: event.target.value })}
              sx={{ flex: 1 }}
            />
            <TextField
              select
              id="lens-schedule-format"
              label="Format"
              size="small"
              value={draft.deliveryFormat ?? 'image_and_pdf'}
              onChange={(event) => update({ deliveryFormat: event.target.value as ScheduleDraft['deliveryFormat'] })}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="image">Images</MenuItem>
              <MenuItem value="pdf">PDF</MenuItem>
              <MenuItem value="image_and_pdf">Images + PDF</MenuItem>
            </TextField>
          </Stack>
          <Box>
            {allowPeriodOverride ? (
              <PeriodFields value={draft.period} onChange={(period) => update({ period })} />
            ) : (
              <Typography variant="body2">
                {draft.period
                  ? 'Uses the date range saved with this schedule.'
                  : 'The report’s own date windows, recalculated for each send.'}
              </Typography>
            )}
          </Box>

          <Box>
            <Typography fontWeight={600} variant="subtitle2">
              Content{draft.filterSets.length > 1 ? ` · ${draft.filterSets.length} versions` : ''}
            </Typography>
            {!hasCards && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                This Lens has no cards yet, so there is nothing to put in a PDF. Add a card to the Lens first.
              </Alert>
            )}
            {hasCards && !hasFilters && (
              <Alert severity="info" sx={{ mb: 1 }}>
                This Lens has no filters, so every PDF would be the same and you can add only one. To make one PDF per
                partner (or per anything else), add that filter to the Lens first.
              </Alert>
            )}
            <Stack spacing={1}>
              {draft.filterSets.map((set, index) => (
                <Stack key={index} spacing={1}>
                  <Stack alignItems="center" direction="row" spacing={1}>
                    {draft.filterSets.length > 1 && (
                      <TextField
                        label="Version name"
                        size="small"
                        sx={{ width: 240 }}
                        value={set.label}
                        inputProps={{ maxLength: SCHEDULE_LIMITS.label }}
                        onChange={(event) =>
                          update({
                            filterSets: draft.filterSets.map((item, at) =>
                              at === index ? { ...item, label: event.target.value } : item,
                            ),
                          })
                        }
                      />
                    )}
                    {supportsTabScope(lens) && (
                      <TextField
                        select
                        id={`lens-schedule-scope-${index}`}
                        label="Tabs"
                        size="small"
                        sx={{ minWidth: 190 }}
                        value={set.scope ?? 'legacy'}
                        onChange={(event) =>
                          update({
                            filterSets: draft.filterSets.map((item, at) =>
                              at === index
                                ? {
                                    ...item,
                                    scope: event.target.value as 'selected' | 'all',
                                    tabs: selectedReportTabs(lens),
                                  }
                                : item,
                            ),
                          })
                        }
                      >
                        {!set.scope && <MenuItem value="legacy">Existing scope</MenuItem>}
                        <MenuItem value="selected">Selected tab</MenuItem>
                        <MenuItem value="all" disabled={!workbookScopeAvailable(lens)}>
                          All tabs
                        </MenuItem>
                      </TextField>
                    )}
                    <Typography color="text.secondary" sx={{ flex: 1, minWidth: 0 }} noWrap variant="body2">
                      {[
                        reportTabSummary(lens, set),
                        Object.keys(set.filters).length || !hasFilters
                          ? describeFilters(lens, set.filters)
                          : 'Lens default filters',
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </Typography>
                    <IconButton
                      aria-label={`Remove ${set.label}`}
                      size="small"
                      onClick={() => update({ filterSets: draft.filterSets.filter((_, at) => at !== index) })}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                  {supportsTabScope(lens) && set.scope && (
                    <Box
                      component="details"
                      sx={{
                        '& summary': { cursor: 'pointer', color: 'text.secondary', typography: 'body2', py: 1 },
                        '&[open] summary': { mb: 1 },
                      }}
                    >
                      <Box component="summary">
                        Layout · A4 ·{' '}
                        {
                          { auto: 'Automatic', portrait: 'Portrait', landscape: 'Landscape' }[
                            (set.print ?? DEFAULT_PRINT_SETTINGS).orientation
                          ]
                        }{' '}
                        ·{' '}
                        {(set.print ?? DEFAULT_PRINT_SETTINGS).imageFit === 'page'
                          ? 'PDF page images'
                          : 'Dashboard images'}
                      </Box>
                      <PrintSettingsFields
                        value={set.print}
                        onChange={(print) =>
                          update({
                            filterSets: draft.filterSets.map((item, at) => (at === index ? { ...item, print } : item)),
                          })
                        }
                        showImage={draft.deliveryFormat !== 'pdf'}
                        showPdf={draft.deliveryFormat !== 'image' || set.print?.imageFit === 'page'}
                      />
                    </Box>
                  )}
                </Stack>
              ))}
            </Stack>
            {draft.filterSets.length > 0 && (
              <Button size="small" sx={{ mt: 1 }} onClick={() => setAddingVersion((value) => !value)}>
                {addingVersion ? 'Done adding versions' : 'Add another version'}
              </Button>
            )}
            {(!draft.filterSets.length || addingVersion) && (
              <Stack direction={{ md: 'row', xs: 'column' }} spacing={1.5} sx={{ mt: 1.5 }}>
                <Button
                  disabled={!hasCards || room <= 0 || (!hasFilters && draft.filterSets.length > 0)}
                  variant="outlined"
                  size="small"
                  onClick={() => addSets([{ label: labelFor(lens, base), filters: base }])}
                >
                  {hasFilters ? 'Add the filters on screen' : 'Add the whole Lens'}
                </Button>
                {hasFilters && (
                  <Button
                    disabled={!hasCards || room <= 0}
                    variant="outlined"
                    size="small"
                    onClick={() => addSets([{ label: 'Report defaults', filters: {} }])}
                  >
                    Add Lens default
                  </Button>
                )}
                {hasCards && selectFilters.length > 0 && (
                  <Stack alignItems="flex-start" direction={{ sm: 'row', xs: 'column' }} spacing={1} sx={{ flex: 1 }}>
                    <TextField
                      select
                      label="One version per"
                      size="small"
                      sx={{ width: { xs: '100%', sm: 160 }, flexShrink: 0 }}
                      value={eachFilterId}
                      onChange={(event) => {
                        setEachFilterId(event.target.value);
                        setEachValues([]);
                      }}
                    >
                      {selectFilters.map((filter) => (
                        <MenuItem key={filter.id} value={filter.id}>
                          {filter.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    {eachFilter && (
                      <Autocomplete
                        multiple
                        disableCloseOnSelect
                        options={eachOptions}
                        value={eachValues}
                        onChange={(_, values) => setEachValues(values)}
                        sx={{ flex: 1, minWidth: 220 }}
                        renderInput={(params) => (
                          <TextField {...params} label={`${eachFilter.label} values`} size="small" />
                        )}
                      />
                    )}
                    {eachFilter && (
                      <Button
                        disabled={!eachOptions.length || eachValues.length === eachOptions.length}
                        size="small"
                        onClick={() => setEachValues(eachOptions)}
                      >
                        All {eachOptions.length}
                      </Button>
                    )}
                    {eachFilter && (
                      <Button
                        disabled={!eachValues.length}
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          addSets(setsForEachValue(lens, base, eachFilter.id, eachValues));
                          setEachValues([]);
                        }}
                      >
                        Add {eachValues.length || ''} version{eachValues.length === 1 ? '' : 's'}
                      </Button>
                    )}
                  </Stack>
                )}
              </Stack>
            )}
            {notice && (
              <Alert severity="info" sx={{ mt: 1 }}>
                {notice}
              </Alert>
            )}
          </Box>

          <Box>
            <Typography fontWeight={600} variant="subtitle2">
              Recipients
            </Typography>
            <Typography color="text.secondary" gutterBottom variant="body2">
              Luxury Escapes email addresses only.
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
              {draft.recipients.map((email) => (
                <Chip
                  key={email}
                  label={email}
                  size="small"
                  onDelete={() => update({ recipients: draft.recipients.filter((item) => item !== email) })}
                />
              ))}
            </Stack>
            <Stack alignItems="flex-start" direction="row" spacing={1}>
              <LensRecipientInput
                value={recipient}
                error={recipientError}
                excludedEmails={draft.recipients}
                onChange={(value) => {
                  setRecipient(value);
                  setRecipientError(null);
                }}
                onSubmit={addRecipient}
              />
              <Button disabled={!recipient.trim()} onClick={addRecipient} sx={{ mt: 0.5 }} variant="outlined">
                Add
              </Button>
            </Stack>
          </Box>

          {isCardLens(lens) && lens.spec.customView && (
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={draft.includeInternal}
                    onChange={(event) => update({ includeInternal: event.target.checked })}
                  />
                }
                label="Include internal figures (for example vendor revenue)"
              />
              <Typography color={draft.includeInternal ? 'warning.main' : 'text.secondary'} variant="body2">
                {draft.includeInternal
                  ? 'The PDFs will show internal figures and the email will say not to send them to partners.'
                  : 'Off: the view is asked to leave out figures it marks as internal. Check a test send before passing PDFs on.'}
              </Typography>
            </Box>
          )}
          <FormControlLabel
            control={<Switch checked={draft.enabled} onChange={(event) => update({ enabled: event.target.checked })} />}
            label={draft.enabled ? 'On: sends automatically' : 'Paused: automatic sends are off'}
          />
          {touched && problem && <Alert severity="warning">{problem}</Alert>}
          {saveError && <Alert severity="error">{saveError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          disabled={saving || !hasCards}
          variant="contained"
          onClick={() => {
            setTouched(true);
            if (!problem) {
              onSave({
                ...draft,
                name: draft.name.trim(),
                filterSets: draft.filterSets.map((set) => ({ ...set, label: set.label.trim() })),
              });
            }
          }}
        >
          {saving ? 'Saving…' : 'Save subscription'}
        </Button>
      </DialogActions>
    </>
  );
}

/**
 * The signed-in person's schedules, owner-only tests and full-recipient manual sends.
 * svc-reporting draws each PDF with the owner's access at the chosen Sydney time.
 */
export default function LensSchedulesDialog({
  open,
  onClose,
  lens,
  screenValues,
  filterOptions,
  userEmail,
  allowPeriodOverride = true,
  initialPeriod,
}: Props) {
  const schedules = useLensSchedules(lens.id, open);
  const save = useSaveLensSchedule(lens.id);
  const remove = useDeleteLensSchedule(lens.id);
  const sendTest = useSendLensScheduleTest(lens.id);
  const sendNow = useSendLensScheduleNow(lens.id);
  const [editing, setEditing] = useState<{ id?: string; draft: ScheduleDraft } | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);
  const [message, setMessage] = useState<{ severity: 'success' | 'error' | 'warning' | 'info'; text: string } | null>(
    null,
  );

  const close = () => {
    setEditing(null);
    setConfirmingDelete(null);
    setMessage(null);
    save.reset();
    onClose();
  };
  const edit = (schedule?: LensSchedule) => {
    setMessage(null);
    save.reset();
    setEditing(
      schedule
        ? {
            id: schedule.id,
            draft: {
              name: schedule.name,
              ...scheduleTiming(schedule),
              weekdays: schedule.weekdays ?? [],
              timezone: schedule.timezone ?? 'Australia/Sydney',
              deliveryFormat: schedule.deliveryFormat ?? 'image_and_pdf',
              dayOfMonth: schedule.dayOfMonth,
              period: schedule.period,
              filterSets: schedule.filterSets,
              recipients: schedule.recipients,
              enabled: schedule.enabled,
              includeInternal: schedule.includeInternal,
            },
          }
        : { draft: { ...emptyDraft(lens, userEmail), period: initialPeriod ?? null } },
    );
  };

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={close}>
      <DialogTitle sx={{ pr: 6 }}>
        {editing ? (editing.id ? 'Edit subscription' : 'New subscription') : 'Subscriptions'}
        <IconButton aria-label="Close" onClick={close} sx={{ position: 'absolute', right: 12, top: 12 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      {editing ? (
        <ScheduleEditor
          filterOptions={filterOptions}
          initial={editing.draft}
          lens={lens}
          saveError={save.error ? errorText(save.error) : null}
          saving={save.isPending}
          screenValues={screenValues}
          allowPeriodOverride={allowPeriodOverride}
          onCancel={() => setEditing(null)}
          onSave={async (draft) => {
            try {
              await save.mutateAsync({ id: editing.id, input: draft });
              setEditing(null);
              setMessage({
                severity: 'success',
                text: `Saved. ${scheduleDescription(draft)}.`,
              });
            } catch {
              // Shown in the editor from save.error.
            }
          }}
        />
      ) : (
        <>
          <DialogContent dividers>
            <Typography color="text.secondary" gutterBottom variant="body2">
              {lens.nativePage
                ? 'Email this report daily, on selected weekdays or monthly, as images, PDFs or both. Enabled schedules keep running until paused or deleted.'
                : 'Email this Lens daily, on selected weekdays or monthly, as images, PDFs or both. They are made with your access, exactly as you would see the Lens.'}{' '}
              Reports go to Luxury Escapes addresses only.
            </Typography>
            {message && (
              <Alert severity={message.severity} sx={{ my: 1 }} onClose={() => setMessage(null)}>
                {message.text}
              </Alert>
            )}
            {schedules.isLoading && <CircularProgress size={24} sx={{ my: 2 }} />}
            {schedules.isError && <Alert severity="error">{errorText(schedules.error)}</Alert>}
            {schedules.data?.length === 0 && (
              <Typography sx={{ my: 2 }} variant="body2">
                You have no scheduled emails on this Lens yet.
              </Typography>
            )}
            <Stack divider={<Divider flexItem />} spacing={1.5} sx={{ mt: 1 }}>
              {(schedules.data ?? []).map((schedule) => {
                const manualRun = activeScheduleRun(schedule.recentRuns, 'manual');
                const testRun = activeScheduleRun(schedule.recentRuns, 'test');
                const uncertainRun = schedule.recentRuns.find((run) => run.status === 'unknown');
                return (
                  <Box key={schedule.id}>
                    <Stack alignItems="center" direction="row" spacing={1}>
                      <Typography fontWeight={600} sx={{ flex: 1 }}>
                        {schedule.name}
                      </Typography>
                      {schedule.includeInternal && (
                        <Chip color="warning" label="Internal figures" size="small" variant="outlined" />
                      )}
                      <Chip
                        color={schedule.enabled ? 'success' : 'default'}
                        label={schedule.enabled ? 'On' : 'Paused'}
                        size="small"
                        variant="outlined"
                      />
                    </Stack>
                    <Typography color="text.secondary" variant="body2">
                      {scheduleDescription(schedule)} ·{' '}
                      {schedule.deliveryFormat === 'image'
                        ? 'Image'
                        : schedule.deliveryFormat === 'pdf'
                          ? 'PDF'
                          : 'Image + PDF'}{' '}
                      · {schedule.filterSets.length} version{schedule.filterSets.length === 1 ? '' : 's'} · to{' '}
                      {schedule.recipients.join(', ')}
                    </Typography>
                    {schedule.ownerEmail !== userEmail?.toLowerCase() && (
                      <Typography color="text.secondary" variant="caption">
                        Created by {schedule.ownerEmail}
                      </Typography>
                    )}
                    {uncertainRun ? (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        {runSummary(uncertainRun)}
                      </Alert>
                    ) : (
                      <Typography variant="body2">{runSummary(schedule.recentRuns[0])}</Typography>
                    )}
                    <Stack direction="row" flexWrap="wrap" spacing={1} sx={{ mt: 1 }}>
                      <Button
                        disabled={sendNow.isPending || Boolean(manualRun)}
                        size="small"
                        variant="contained"
                        onClick={async () => {
                          try {
                            const run = await sendNow.mutateAsync(schedule.id);
                            setMessage({
                              severity: runSeverity(run),
                              text:
                                run.status === 'queued'
                                  ? `Send queued for all ${schedule.recipients.length} recipient${schedule.recipients.length === 1 ? '' : 's'}. Follow its status below.`
                                  : runSummary(run),
                            });
                          } catch (error) {
                            setMessage({ severity: 'error', text: errorText(error) });
                          }
                        }}
                      >
                        {manualRun?.status === 'unknown'
                          ? 'Awaiting confirmation'
                          : manualRun
                            ? 'Send in progress'
                            : 'Send now'}
                      </Button>
                      <Button
                        disabled={sendTest.isPending || Boolean(testRun)}
                        size="small"
                        onClick={async () => {
                          try {
                            const run = await sendTest.mutateAsync(schedule.id);
                            setMessage({
                              severity: runSeverity(run),
                              text:
                                run.status === 'queued'
                                  ? `Test queued for ${schedule.ownerEmail} only. Follow its status below.`
                                  : runSummary(run),
                            });
                          } catch (error) {
                            setMessage({ severity: 'error', text: errorText(error) });
                          }
                        }}
                      >
                        {schedule.ownerEmail === userEmail?.toLowerCase()
                          ? 'Send a test to me'
                          : 'Send a test to owner'}
                      </Button>
                      <Button size="small" onClick={() => edit(schedule)}>
                        Edit
                      </Button>
                      {confirmingDelete === schedule.id ? (
                        <>
                          <Button
                            color="error"
                            size="small"
                            variant="contained"
                            onClick={async () => {
                              try {
                                await remove.mutateAsync(schedule.id);
                                setMessage({ severity: 'success', text: `Deleted “${schedule.name}”.` });
                              } catch (error) {
                                setMessage({ severity: 'error', text: errorText(error) });
                              } finally {
                                setConfirmingDelete(null);
                              }
                            }}
                          >
                            Delete for good
                          </Button>
                          <Button size="small" onClick={() => setConfirmingDelete(null)}>
                            Keep
                          </Button>
                        </>
                      ) : (
                        <Button color="error" size="small" onClick={() => setConfirmingDelete(schedule.id)}>
                          Delete
                        </Button>
                      )}
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={close}>Close</Button>
            <Button variant="contained" onClick={() => edit()}>
              New subscription
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
